import { MutableRefObject, useCallback, useEffect, useState } from 'react';

/**
 * Hook for NavScroller to calculate available vertical height.
 *
 * WHY THIS EXISTS:
 * NavScroller is used in database destinations where content loads dynamically
 * after initial render. When a user selects a table, additional sections
 * ("Update Mode", "Format Table") appear, shifting the layout. The generic
 * `useDistance` hook doesn't recalculate when this happens, causing incorrect
 * height calculations and whitespace at the bottom.
 *
 * HOW IT WORKS:
 * 1. Finds the containing block (modal with position: fixed/absolute)
 * 2. Calculates available height from scrollContainer's position to container bottom
 * 3. Uses ResizeObserver to recalculate when layout changes (content loads/shifts)
 *
 * @param ref - Reference to the scrollable element
 * @param defaultHeight - Initial height before calculation (default: 0)
 * @returns Available height in pixels
 */
export function useNavScrollerHeight(ref: MutableRefObject<HTMLElement>, defaultHeight = 0) {
  const [distance, setDistance] = useState(defaultHeight);

  const onResize = useCallback(() => {
    if (!ref?.current) return;

    const scrollContainer = ref.current.parentElement;
    if (!scrollContainer) return;

    const scrollContainerRect = scrollContainer.getBoundingClientRect();

    // Walk up the DOM tree to find a positioned ancestor (modal/dialog container)
    // This is framework-agnostic - works with MUI, custom modals, or any positioned container
    let containingBlock = null;
    let parent = scrollContainer.parentElement;

    while (parent && parent !== document.body) {
      const position = window.getComputedStyle(parent).position;
      if (position === 'fixed' || position === 'absolute') {
        containingBlock = parent;
        break;
      }
      parent = parent.parentElement;
    }

    if (containingBlock) {
      // MODAL CONTEXT: Calculate from modal bounds
      // Available height = modal height - space consumed above scrollContainer
      const containingRect = containingBlock.getBoundingClientRect();
      const spaceAboveScrollContainer = scrollContainerRect.top - containingRect.top;
      setDistance(containingRect.height - spaceAboveScrollContainer);
    } else {
      // FULL PAGE CONTEXT: Calculate from viewport
      // Available height = viewport height - scrollContainer top offset
      setDistance(window.innerHeight - scrollContainerRect.top);
    }
  }, [ref]);

  useEffect(() => {
    if (!ref?.current) return;

    // Listen for window resize events
    window.addEventListener('resize', onResize);

    // ResizeObserver detects when observed elements change size
    // This fires when:
    // - New sections are added (e.g., "Update Mode", "Format Table")
    // - Existing sections expand/collapse
    // - Modal size changes
    const resizeObserver = new ResizeObserver(onResize);

    const scrollContainer = ref.current.parentElement;
    if (scrollContainer) {
      // Observe scrollContainer: detects when content inside changes
      resizeObserver.observe(scrollContainer);

      // Observe parent: detects when siblings are added/removed
      if (scrollContainer.parentElement) {
        resizeObserver.observe(scrollContainer.parentElement);
      }
    }

    // Calculate initial height
    onResize();

    return () => {
      window.removeEventListener('resize', onResize);
      resizeObserver.disconnect();
    };
  }, [onResize]);

  return distance;
}
